import { useCallback, useEffect, useState } from 'react';

/**
 * Where a banner's dismissed state is persisted.
 *
 * - `'session'` — remembered for the current browser session only (sessionStorage).
 *   The banner stays hidden across navigations but reappears in a new session.
 * - `'local'` — remembered permanently across sessions (localStorage).
 */
type BannerPersistence = 'session' | 'local';

interface UseDismissibleBannerResult {
  isDismissed: boolean;
  dismiss: () => void;
}

const STORAGE_KEY_PREFIX = 'flora-banner-dismissed';

/**
 * Resolves the storage backend for a given persistence mode, guarding against
 * environments where Web Storage is unavailable (SSR, privacy modes).
 *
 * @param {BannerPersistence} persistence - Which storage backend to use
 * @returns {Storage | null} The matching Storage object, or null when unavailable
 */
function getStorage(persistence: BannerPersistence): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return persistence === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Builds the storage key for a banner id so multiple banners never collide.
 *
 * @param {string} bannerId - Caller-provided unique banner identifier
 * @returns {string} The namespaced storage key
 */
function getStorageKey(bannerId: string): string {
  return `${STORAGE_KEY_PREFIX}:${bannerId}`;
}

/**
 * Reads the dismissed flag for a storage key, failing closed (not dismissed)
 * when storage is unavailable or the read throws.
 *
 * @param {BannerPersistence} persistence - Which storage backend to read from
 * @param {string} storageKey - The namespaced storage key to read
 * @returns {boolean} Whether the banner has been dismissed
 */
function readDismissed(persistence: BannerPersistence, storageKey: string): boolean {
  const storage = getStorage(persistence);
  try {
    return storage?.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
}

/**
 * Tracks the dismissed state of a one-off informational banner so it can be
 * hidden once the user closes it and stay hidden for the chosen persistence
 * window. Reusable for any Flora banner — pass a stable, unique `bannerId`.
 *
 * The dismissed flag re-syncs whenever `bannerId` changes, so callers that swap
 * the id without remounting (e.g. an org switch that only updates a store) get
 * the correct per-id value rather than the first id's stale state.
 *
 * @param {string} bannerId - Unique id for this banner (e.g. `complete-preferences:${orgId}`)
 * @param {BannerPersistence} [persistence] - Dismissal lifetime; defaults to `'session'`
 * @returns {UseDismissibleBannerResult} The current dismissed flag and a `dismiss` callback
 */
function useDismissibleBanner(
  bannerId: string,
  persistence: BannerPersistence = 'session',
): UseDismissibleBannerResult {
  const storageKey = getStorageKey(bannerId);
  const [isDismissed, setIsDismissed] = useState(() => readDismissed(persistence, storageKey));

  // Re-sync when the id/persistence changes. The consuming component may not
  // remount on such a change (org switches update a store, not the route), so
  // the once-only useState initializer would otherwise serve a stale value.
  useEffect(() => {
    setIsDismissed(readDismissed(persistence, storageKey));
  }, [persistence, storageKey]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    const storage = getStorage(persistence);
    try {
      storage?.setItem(storageKey, 'true');
    } catch {
      // Storage write can fail (quota, privacy mode); the in-memory state still
      // hides the banner for the current render, which is good enough.
    }
  }, [persistence, storageKey]);

  return { isDismissed, dismiss };
}

export { useDismissibleBanner };
